import { useEffect, useRef, useState } from "react";
import { Send, Loader2, Sparkles, RotateCcw } from "lucide-react";
import { API, api } from "@/lib/apiClient";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const newSession = () => `cs-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const SUGGEST = [
  "Preventivo per una coppia di 44 e 41 anni con un figlio di 10, pagamento semestrale",
  "Che differenza c'è tra Salute e Sostegno?",
  "Quali sono le carenze per un ricovero da malattia?",
];
const INTRO = "Chiedi un preventivo o informazioni sui piani 4You. Usa solo ruolo ed età, mai nomi o dati sulla salute. Gli importi arrivano sempre dal motore di calcolo; le risposte AI vanno comunque verificate.";

// Assistente 4You: l'AI usa la Skill; gli importi vengono sempre dal motore di calcolo del portale.
// Senza props è la versione Partner; nell'area riservata dell'accordo usa l'endpoint pubblico.
export function CoopSaluteChat({ endpoint = "/coopsalute/chat", extraBody = {}, historyQuery = "", storageKey = "cs_session", suggest = SUGGEST, intro = INTRO }) {
  const [sid, setSid] = useState(() => localStorage.getItem(storageKey) || newSession());
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  const sending = useRef(false);
  useEffect(() => {
    localStorage.setItem(storageKey, sid);
    api.get(`${endpoint}/${sid}${historyQuery}`).then(({ data }) => { if (!sending.current) setMsgs(data.messages); }).catch(() => {});
  }, [sid, endpoint, historyQuery, storageKey]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [msgs]);

  const send = async (q) => {
    const message = (q ?? text).trim();
    if (!message || busy) return;
    sending.current = true;
    setText(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "" }]);
    const append = (t) => setMsgs((m) => {
      const c = [...m];
      if (!c.length || c[c.length - 1].role !== "assistant") c.push({ role: "assistant", text: "" });
      c[c.length - 1] = { ...c[c.length - 1], text: c[c.length - 1].text + t };
      return c;
    });
    try {
      const r = await fetch(`${API}${endpoint}`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...extraBody, session_id: sid, message }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Errore");
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        parts.forEach((p) => {
          if (!p.startsWith("data: ")) return;
          const d = JSON.parse(p.slice(6));
          if (d.t) append(d.t);
          if (d.error) append(d.error);
        });
      }
    } catch (e) { append(`⚠ ${e.message}`); } finally { setBusy(false); sending.current = false; }
  };

  return (
    <div className="flex h-[70vh] min-h-[480px] flex-col rounded-2xl border bg-white" data-testid="cs-chat">
      <div className="flex items-center justify-between border-b px-5 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-forest"><Sparkles className="h-4 w-4 text-terracotta" />Assistente 4You</div>
        <button onClick={() => { setSid(newSession()); setMsgs([]); }} disabled={busy} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-forest disabled:opacity-40" data-testid="cs-chat-new"><RotateCcw className="h-3.5 w-3.5" />Nuova conversazione</button>
      </div>
      <div className="flex-1 space-y-4 overflow-y-auto p-5" data-testid="cs-chat-messages">
        {msgs.length === 0 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{intro}</p>
            {suggest.map((s, i) => <button key={i} onClick={() => send(s)} data-testid={`cs-suggest-${i}`} className="block w-full rounded-lg border px-3 py-2 text-left text-sm text-forest hover:bg-sage">{s}</button>)}
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : ""} data-testid={`cs-msg-${i}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${m.role === "user" ? "whitespace-pre-wrap bg-forest text-white" : "cs-md bg-sage text-forest"}`}>
              {!m.text ? <Loader2 className="h-4 w-4 animate-spin" /> : m.role === "user" ? m.text : <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.text}</ReactMarkdown>}
            </div>
          </div>
        ))}
        <div ref={end} />
      </div>
      <div className="flex gap-2 border-t p-3">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Scrivi la richiesta…" data-testid="cs-chat-input"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          className="flex-1 resize-none rounded-lg border border-input px-3 py-2 text-sm outline-none focus:border-forest" />
        <button onClick={() => send()} disabled={busy || !text.trim()} data-testid="cs-chat-send" className="grid w-12 place-items-center rounded-lg bg-terracotta text-white hover:bg-terracotta-hover disabled:opacity-50" aria-label="Invia">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}
